import dayjs from 'dayjs';
import type { TypeCalendarView } from '@/types/calendar/types.calendar';

export const DAY_KEY = 'YYYY-MM-DD';

export const dayKey = (date: Date | string): string => dayjs(date).format(DAY_KEY);

export const parseDayKey = (key: string | null): Date | null => {
    if (!key || !/^\d{4}-\d{2}-\d{2}$/.test(key)) return null;
    const parsed = dayjs(key);
    return parsed.isValid() ? parsed.startOf('day').toDate() : null;
};

export const startOfDay = (date: Date): Date => dayjs(date).startOf('day').toDate();

export const addDays = (date: Date, days: number): Date => dayjs(date).add(days, 'day').toDate();

export const startOfWeek = (date: Date): Date => {
    const day = dayjs(date).startOf('day');
    const offset = (day.day() + 6) % 7;
    return day.subtract(offset, 'day').toDate();
};

export const isSameDay = (a: Date | string, b: Date | string): boolean => dayjs(a).isSame(b, 'day');

export const daysFrom = (start: Date, count: number): Date[] =>
    Array.from({ length: count }, (_, index) => addDays(start, index));

export const monthGrid = (anchor: Date): Date[] => daysFrom(startOfWeek(dayjs(anchor).startOf('month').toDate()), 42);

export type TypeRange = { from: Date; to: Date };

export const AGENDA_DAYS = 14;

export function rangeFor(view: TypeCalendarView, anchor: Date, weekDays: number): TypeRange {
    const day = startOfDay(anchor);
    if (view === 'giorno') return { from: day, to: addDays(day, 1) };
    if (view === 'agenda') return { from: day, to: addDays(day, AGENDA_DAYS) };
    if (view === 'mese') {
        const grid = monthGrid(day);
        return { from: grid[0], to: addDays(grid[grid.length - 1], 1) };
    }
    if (weekDays >= 7) {
        const from = startOfWeek(day);
        return { from, to: addDays(from, 7) };
    }
    return { from: day, to: addDays(day, weekDays) };
}

export function visibleDays(view: TypeCalendarView, anchor: Date, weekDays: number): Date[] {
    if (view === 'mese') return monthGrid(anchor);
    const range = rangeFor(view, anchor, weekDays);
    return daysFrom(range.from, dayjs(range.to).diff(range.from, 'day'));
}

export function shiftAnchor(view: TypeCalendarView, anchor: Date, direction: 1 | -1, weekDays: number): Date {
    const day = dayjs(anchor);
    if (view === 'mese') return day.add(direction, 'month').startOf('month').toDate();
    if (view === 'giorno') return day.add(direction, 'day').toDate();
    if (view === 'agenda') return day.add(direction * AGENDA_DAYS, 'day').toDate();
    return day.add(direction * weekDays, 'day').toDate();
}

export function rangeTitle(view: TypeCalendarView, anchor: Date, weekDays: number, short = false): string {
    const day = dayjs(anchor);
    const month = short ? 'MMM' : 'MMMM';
    if (view === 'mese') return day.format('MMMM YYYY');
    if (view === 'giorno') return day.format(short ? 'ddd D MMM YYYY' : 'dddd D MMMM YYYY');
    const range = rangeFor(view, anchor, weekDays);
    const from = dayjs(range.from);
    const to = dayjs(range.to).subtract(1, 'day');
    if (from.isSame(to, 'month')) return `${from.format('D')} – ${to.format(`D ${month} YYYY`)}`;
    if (from.isSame(to, 'year')) return `${from.format('D MMM')} – ${to.format('D MMM YYYY')}`;
    return `${from.format('D MMM YYYY')} – ${to.format('D MMM YYYY')}`;
}

export const overlapsRange = (startAt: string, endAt: string, range: TypeRange): boolean =>
    Date.parse(startAt) < range.to.getTime() && Date.parse(endAt) > range.from.getTime();
